import type { CatalogCard } from "@pokifolio/contracts";
import { Loader2, Search } from "lucide-react";
import { type SubmitEvent, useId, useState } from "react";

import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { serverFieldError } from "../../lib/errors";
import { CardLookupResults } from "./card-lookup-results";
import { MIN_LOOKUP_LENGTH, useCardLookup } from "./search-queries";

interface CardPickerProps {
  readonly onPickCard: (card: CatalogCard) => void;
  readonly onSearchWords: (words: string) => void;
  /** A listing search is running. */
  readonly busy: boolean;
  readonly initialText?: string;
  /** False once a card is picked: the matching cards fold away until the person types or changes the card. */
  readonly showMatches?: boolean;
  /** Called on every change to the text, so the matching cards open again. */
  readonly onTyping?: () => void;
}

/**
 * Step 1: the employee types a card name and number; matching catalog cards appear with pictures as they type
 * (after a short pause). Picking one searches for exactly that card. "Search these words" searches the text as
 * typed. Pressing Enter picks the card when exactly one matches, otherwise searches the words.
 */
export function CardPicker({
  onPickCard,
  onSearchWords,
  busy,
  initialText = "",
  showMatches = true,
  onTyping,
}: CardPickerProps) {
  const [text, setText] = useState(initialText);
  const lookup = useCardLookup(text);
  const inputId = useId();
  const hintId = useId();
  const cards = lookup.enabled ? (lookup.data ?? []) : [];
  const words = text.trim();
  const fieldError = lookup.isError ? serverFieldError(lookup.error, "q") : undefined;

  function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    // One listing search at a time: Enter or a second click while one is running does nothing.
    if (words.length < MIN_LOOKUP_LENGTH || busy) return;
    const [only] = cards;
    if (cards.length === 1 && only !== undefined && !lookup.isPlaceholderData) onPickCard(only);
    else onSearchWords(words);
  }

  return (
    <Card className="flex flex-col gap-4">
      <form onSubmit={submit} className="flex flex-col gap-2" role="search">
        <label htmlFor={inputId} className="text-sm font-medium text-foreground">
          Card name and number
        </label>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            id={inputId}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              onTyping?.();
            }}
            placeholder="e.g. Venusaur EX XY28 or Charizard 4/102"
            autoComplete="off"
            aria-describedby={hintId}
            aria-invalid={fieldError !== undefined}
          />
          <Button
            type="submit"
            disabled={words.length < MIN_LOOKUP_LENGTH || busy}
            aria-busy={busy}
            className="shrink-0"
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <Search className="size-4" aria-hidden="true" />
            )}
            Search these words
          </Button>
        </div>
        <p id={hintId} className="text-xs text-muted-foreground">
          Adding the number printed on the card (like XY28 or 4/102) finds the exact card.
        </p>
      </form>

      {showMatches && (
        <CardLookupResults
          lookup={lookup}
          busy={busy}
          actionLabel={(card) => `Search for ${card.name} ${card.number}, ${card.setName}`}
          onChoose={onPickCard}
          emptyHint="Check the spelling, or use “Search these words”."
        />
      )}
    </Card>
  );
}
